import { Component } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import Validation from '../../admin/forms/add-user/validation';
import { passwordStrengthValidator } from '../../../legacy/password-validation/password-strength.validator';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { AppConfigQuery } from '../../../core/state/app-config/app-config.query';

@Component({
    selector: 'app-sb-reset-password',
    styles: [
        `
            .card {
                max-width: 375px;
                min-width: auto;
                /* TODO: use utility classes */
                margin-top: 5rem;
                margin-left: auto;
                margin-right: auto;
            }
        `
    ],
    template: `
        <div class="card">
            <header>
                <h2>Password Reset</h2>
            </header>
            <main>
                <form
                    [formGroup]="resetPasswordForm"
                    autocomplete="off"
                    id="resetPasswordForm"
                    (ngSubmit)="submit()"
                >
                    <mat-form-field appearance="fill">
                        <mat-label>Password</mat-label>
                        <input
                            matInput
                            formControlName="password"
                            type="password"
                            autocomplete="new-password"
                        />
                        <mat-hint>Choose a new password</mat-hint>
                        <mat-error
                            *ngIf="resetPasswordForm.get('password').errors as errors"
                        >
                            <ng-container *ngIf="errors.required">
                                Field is required
                            </ng-container>
                            <ng-container *ngIf="errors.minlength as minLength">
                                Password needs to be at least
                                {{ minLength.requiredLength }} characters long
                            </ng-container>
                            <ng-container
                                *ngIf="!errors.minlength && errors.passwordStrength"
                            >
                                Password is not strong enough
                            </ng-container>
                        </mat-error>
                    </mat-form-field>
                    <mat-form-field appearance="fill" class="mt-3">
                        <mat-label>Confirm Password</mat-label>
                        <input
                            matInput
                            formControlName="confirmPassword"
                            type="password"
                            autocomplete="new-password"
                        />
                        <mat-hint
                            *ngIf="
                                resetPasswordForm.value.confirmPassword.length === 0 ||
                                    resetPasswordForm.value.password !==
                                        resetPasswordForm.value.confirmPassword;
                                else hintMatch
                            "
                        >
                            Retype same password entered for password field
                        </mat-hint>
                        <mat-error
                            *ngIf="
                                resetPasswordForm.get('confirmPassword').errors?.required
                            "
                        >
                            Field is required
                        </mat-error>
                        <mat-error *ngIf="resetPasswordForm.errors?.matching">
                            Confirm password differs from entered password
                        </mat-error>
                    </mat-form-field>
                    <app-password-validation
                        [enableFeedback]="true"
                        [minPasswordLength]="minLength"
                        [password]="resetPasswordForm.value.password"
                    ></app-password-validation>
                    <div
                        role="alert"
                        class="critical mt-3"
                        *ngIf="resetPasswordForm.invalid && isSubmitted"
                    >
                        <h4 class="mb-0">Password was not reset</h4>
                        <ng-container
                            *ngIf="resetPasswordForm.errors?.server; else formError"
                        >
                            {{ this.resetPasswordForm.errors.server }}
                        </ng-container>
                    </div>
                    <div
                        role="alert"
                        class="success mt-3"
                        *ngIf="passwordReset && isSubmitted"
                    >
                        <h4 class="mb-0">Password has been reset</h4>
                        You can now use the new password to login
                    </div>
                </form>
            </main>
            <footer class="mt-3">
                <a class="btn secondary" routerLink="/login">Go to login</a>
                <button
                    class="btn primary"
                    form="resetPasswordForm"
                    type="submit"
                    [disabled]="isSubmitted || passwordReset"
                >
                    Reset password
                </button>
            </footer>
        </div>
        <ng-template #hintMatch>
            <mat-hint class="text-success">Passwords match</mat-hint>
        </ng-template>
        <ng-template #formError>
            The form contains one or more errors, please correct them to reset password.
        </ng-template>
    `
})
export class SbResetPasswordComponent {
    isSubmitted = false;
    unsubscribe$ = new Subject();
    minLength: number = 4;
    resetPasswordForm: UntypedFormGroup;
    passwordReset = false;

    constructor(
        private router: Router,
        private http: HttpClient,
        private config: AppConfigQuery,
        private _fb: UntypedFormBuilder
    ) {
        this.resetPasswordForm = this._fb.group(
            {
                password: [
                    '',
                    [
                        Validators.required,
                        Validators.minLength(this.minLength),
                        passwordStrengthValidator()
                    ]
                ],
                confirmPassword: ['', [Validators.required]]
            },
            {
                validators: [Validation.match('password', 'confirmPassword')]
            }
        );

        this.resetPasswordForm.valueChanges
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((_) => {
                this.isSubmitted = false;
                this.resetPasswordForm.setErrors(null);
            });
    }

    submit() {
        this.isSubmitted = true;
        if (this.resetPasswordForm.valid)
            this.http
                .post(
                    this.config.get().gatewayBaseUrl + '/password/save',
                    { password: this.resetPasswordForm.value.password },
                    { withCredentials: true }
                )
                .subscribe(
                    (response) => {
                        if (response.hasOwnProperty('error')) {
                            this.resetPasswordForm.setErrors({
                                server: response['error']
                            });
                        } else if (response.hasOwnProperty('message')) {
                            this.passwordReset = true;
                        }
                    },
                    (error) => {
                        this.resetPasswordForm.setErrors({
                            server: error.message
                        });
                    }
                );
    }

    ngOnDestroy() {
        this.unsubscribe$.next(true);
        this.unsubscribe$.complete();
    }
}
